import { timestampDTOSchema } from '@tempo-io/common-dto';
import { z } from 'zod';
import { findingGroups } from './finding-dto';

/**
 * One unified lifecycle for both agent-inferred continuity and user-asserted tracking: `pinned` is
 * simply an early state in the same enum as `assigned`/`completed`/`dismissed`, not a separate
 * system (Dima's framing, 2026-09-16 persistence meeting). `agent_tracked` is the default row state
 * before any user acts. `auto_resolved`/`superseded` are pipeline-written only — see
 * `humanSettableTrackedItemStatuses` for what a caller may set directly.
 */
export const trackedItemStatuses = [
  'agent_tracked',
  'pinned',
  'assigned',
  'in_motion',
  'completed',
  'dismissed',
  'auto_resolved',
  'superseded',
] as const;
export type TrackedItemStatus = (typeof trackedItemStatuses)[number];
export const trackedItemStatusSchema = z.enum(trackedItemStatuses);

export const humanSettableTrackedItemStatuses = [
  'pinned',
  'assigned',
  'in_motion',
  'completed',
  'dismissed',
] as const;
export type HumanSettableTrackedItemStatus = (typeof humanSettableTrackedItemStatuses)[number];
export const humanSettableTrackedItemStatusSchema = z.enum(humanSettableTrackedItemStatuses);

/**
 * Verification state of a tracked item's grounding snapshot. `unverified` until the deterministic
 * replay engine ever runs against it; the rest are the verifier's own verdicts. Only `unverified` is
 * written today — this is a seam for that later workstream, not yet load-bearing.
 */
export const groundingStatuses = [
  'unverified',
  'holding',
  'patched',
  'resolved',
  'invalidated',
  'needsReview',
] as const;
export type GroundingStatus = (typeof groundingStatuses)[number];
export const groundingStatusSchema = z.enum(groundingStatuses);

/** Whether a status transition was made by a person or written by the pipeline (e.g. a future auto-resolve). */
export const trackedItemActorTypes = ['user', 'system'] as const;
export type TrackedItemActorType = (typeof trackedItemActorTypes)[number];
export const trackedItemActorTypeSchema = z.enum(trackedItemActorTypes);

export const trackedItemDTOSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  loopId: z.number().int().positive().nullable(),
  findingId: z.string(),
  /** null = the whole finding is tracked; set = one specific recommendation inside it. */
  recommendationId: z.string().nullable(),
  status: trackedItemStatusSchema,
  assigneeUserId: z.string().nullable(),
  createdByUserId: z.string(),
  lastActorUserId: z.string().nullable(),
  dueAt: z.string().nullable(),
  note: z.string().nullable(),
  /** Frozen at write time — lets the row render even if the source finding is later retired,
   * capped out of a run, or its run becomes unreadable on a future schema-version bump. */
  findingHeadlineSnapshot: z.string(),
  recommendationTitleSnapshot: z.string().nullable(),
  /** Soft reference only, no FK: the run row this came from is allowed to become unreadable or be
   * pruned without taking the tracked item down with it. */
  sourceRunId: z.string(),
  /** Captured at pin time from the finding's own group; the evidence-replay comparator reads this to
   * decide what counts as "resolved" for this tracked item. */
  findingGroup: z.enum(findingGroups).nullable(),
  /** The evidence-replay engine's captured fact contract, copied by value at pin time. Left untyped
   * here (rather than importing findingGroundingSchema) to avoid a dependency the replay workstream
   * doesn't need yet; that engine parses/validates its own shape on read. */
  groundingSnapshot: z.record(z.string(), z.unknown()).nullable(),
  groundingStatus: groundingStatusSchema,
  currentValue: z.union([z.number(), z.string(), z.boolean()]).nullable(),
  lastVerifiedAt: z.string().nullable(),
  lastVerifiedRunId: z.string().nullable(),
  resolutionNote: z.string().nullable(),
  /** Set on the first replay cycle that finds the underlying fact missing; two consecutive
   * occurrences finalize `groundingStatus: 'invalidated'` rather than one bad read doing so. */
  pendingInvalidationSince: z.string().nullable(),
  ...timestampDTOSchema.shape,
});
export type TrackedItemDTO = z.infer<typeof trackedItemDTOSchema>;

export const trackedItemEventDTOSchema = z.object({
  id: z.string(),
  trackedItemId: z.string(),
  organizationId: z.string(),
  fromStatus: trackedItemStatusSchema.nullable(),
  toStatus: trackedItemStatusSchema,
  actorType: trackedItemActorTypeSchema,
  actorUserId: z.string().nullable(),
  note: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  ...timestampDTOSchema.shape,
});
export type TrackedItemEventDTO = z.infer<typeof trackedItemEventDTOSchema>;

export const trackedItemDetailDTOSchema = trackedItemDTOSchema.extend({
  history: z.array(trackedItemEventDTOSchema),
});
export type TrackedItemDetailDTO = z.infer<typeof trackedItemDetailDTOSchema>;

// `agent_tracked`/`auto_resolved`/`superseded` are deliberately excluded — a caller cannot request
// either directly, matching `humanSettableTrackedItemStatuses`.
export const setTrackedItemStatusBodySchema = z
  .object({
    status: humanSettableTrackedItemStatusSchema,
    assigneeUserId: z.string().min(1).optional(),
    note: z.string().trim().min(1).max(1000).optional(),
    dueAt: z.string().datetime().optional(),
  })
  .refine((body) => body.status !== 'assigned' || body.assigneeUserId !== undefined, {
    message: 'assigneeUserId is required when status is assigned',
  });
export type SetTrackedItemStatusBody = z.output<typeof setTrackedItemStatusBodySchema>;
